import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { ARTIST_SUGGESTIONS_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/artistSuggestionsColumnsConfig';
import { buildArtistSuggestionsFilterGroups } from '../constants/artistSuggestionsFiltersConfig';

const axios = require('axios').default;

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  if (activeFilters.on_tour_only && activeFilters.on_tour_only.length > 0) {
    params.on_tour_only = 'true';
  }
  if (activeFilters.never_played_here_only && activeFilters.never_played_here_only.length > 0) {
    params.never_played_here_only = 'true';
  }
  return params;
};

const TouringBadge = () => (
  <span className="badge badge-success">On tour</span>
);

const ARTIST_SUGGESTIONS_CONFIG = {
  pageTitle: 'Artist Suggestions',
  pageSubtitle: 'Artists worth booking. Peer signal looks back 18 months at similar venues; "Never played here" excludes artists who headlined this venue in the last 12 months.',
  sidebarKey: 'booking_artist_suggestions',
  defaultPreset: 'next_90_days',
  dateModeOptions: [],
  defaultSortRules: [{ field: 'peer_score', direction: 'desc' }],

  columns: ARTIST_SUGGESTIONS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingBookingArtistSuggestions_${teamId}`,
  firstColumnId: 'name',

  kpiFromDetailResponse: true,
  kpiGridColumns: 4,
  buildKpiCards: (current = {}) => [
    { label: 'Suggestions', value: (current.total_suggestions || 0).toLocaleString(), current: current.total_suggestions || 0 },
    { label: 'Currently Touring', value: (current.on_tour || 0).toLocaleString(), current: current.on_tour || 0 },
    { label: 'With Peer History', value: (current.with_peer_history || 0).toLocaleString(), current: current.with_peer_history || 0 },
    { label: 'Avg Peer Score', value: (current.average_peer_score || 0).toFixed(2), current: current.average_peer_score || 0 },
  ],

  salesDataKey: null,
  venueSeriesKey: null,
  overTimeKey: null,
  topCharts: [],
  groupByOptions: null,
  groupByDefault: null,

  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/booking/artist_suggestions/data.json`,
  detailResponseKey: 'suggestions',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/booking/artist_suggestions/export`,
  salesExportSection: 'artist_suggestions',
  detailErrorMessage: 'Failed to load artist suggestions.',
  detailTableTitle: 'Artist Suggestions',
  emptyMessage: 'No artists match these filters. This venue may not have a peer set yet — try a different venue, or contact support to compute peers for this one.',
  searchPlaceholder: 'Search artists...',
  paginationLabel: 'artists',
  itemLabel: (count) => `artist${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  filterTitle: 'Filter Suggestions',
  filterSubtitle: 'Pick a venue and narrow the artist list.',

  renderCellValue: (col, row, { formatEventDate }) => {
    const peerNames = (row.peer_venue_names || []);
    switch (col.id) {
      case 'name':
        if (!row.name) return '—';
        return row.permalink
          ? <a href={`/artists/${row.permalink}`}>{row.name}</a>
          : row.name;
      case 'peer_score':
        return (row.peer_score || 0).toFixed(2);
      case 'peer_venue_count':
        if (!row.peer_venue_count) return '—';
        return (
          <div>
            <div>{row.peer_venue_count}</div>
            {peerNames.length > 0 && (
              <div className="text-muted" style={{ fontSize: 12 }} title={peerNames.join(', ')}>
                {peerNames.slice(0, 3).join(', ')}
                {peerNames.length > 3 ? ` +${peerNames.length - 3} more` : ''}
              </div>
            )}
          </div>
        );
      case 'peer_last_show':
        return row.peer_last_show ? formatEventDate(row.peer_last_show) : '—';
      case 'on_tour':
        return row.on_tour ? <TouringBadge /> : '—';
      case 'average_capacity_last_12_months': {
        const cap = row.average_capacity_last_12_months;
        return cap == null ? '—' : Math.round(cap).toLocaleString();
      }
      case 'last_show_date':
        return row.last_show_date ? formatEventDate(row.last_show_date) : '—';
      default:
        return '—';
    }
  },
};

function ReportingBookingArtistSuggestionsPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/booking/artist_suggestions/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((err) => console.error('Failed to load artist suggestions filter options', err));
  }, [team && team.id, csrfToken]);

  const configWithFilters = {
    ...ARTIST_SUGGESTIONS_CONFIG,
    buildFilterGroups: () => buildArtistSuggestionsFilterGroups(filterOptions),
  };

  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingBookingArtistSuggestionsPage;
